import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  ShieldCheck,
  ShieldAlert,
  MessageSquare,
  CreditCard,
  ChevronRight,
  User,
  Settings,
} from "lucide-react";
import IbanForm from "@/components/iban/IbanForm";
import SecuritySettingsCard from "@/components/security/SecuritySettingsCard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { tAuto } from "@/lib/tAuto";
import { hasProfileSubmittedKyc, isProfileKycVerified, isProfileVerificationPending } from "@/lib/kycStatus";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ─── Section wrapper ─── */
const SectionCard = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={cn("rounded-3xl bg-white dark:bg-[#151518] border border-gray-200/50 dark:border-zinc-800/40 overflow-hidden shadow-sm", className)}>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, iconBg }: { icon: React.ComponentType<any>; title: string; subtitle?: string; iconBg?: string }) => (
  <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100 dark:border-zinc-800/40">
    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shadow-sm", iconBg || "bg-gradient-to-br from-primary/15 to-primary/5")}>
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <div>
      <h3 className="text-sm font-bold text-gray-800 dark:text-zinc-100 font-display">{title}</h3>
      {subtitle && <p className="text-[11px] text-zinc-450 dark:text-zinc-500 font-semibold mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

/* ─── Sidebar nav item ─── */
const SidebarItem = ({ icon: Icon, label, active, onClick }: { icon: React.ComponentType<any>; label: string; active: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-xs font-bold transition-all duration-200 text-left border",
      active
        ? "bg-primary text-white border-primary/10 shadow-sm"
        : "text-zinc-500 dark:text-zinc-400 border-transparent hover:text-gray-900 dark:hover:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-800/50"
    )}
  >
    <Icon className="w-4 h-4 shrink-0" />
    <span>{label}</span>
  </button>
);

export function HostSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [activeSection, setActiveSection] = useState("conta");
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState("");
  const [isSavingAutoReply, setIsSavingAutoReply] = useState(false);
  const [showAutoReplyDialog, setShowAutoReplyDialog] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (profile) {
      setAutoReplyEnabled(profile.auto_reply_enabled || false);
      setAutoReplyMessage(profile.auto_reply_message || "");
    }
  }, [profile]);

  const updateProfile = useMutation({
    mutationFn: async (updates: any) => {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["user-profile", user?.id] });
    },
    onError: () => {
      toast({ title: "Erro", description: tAuto("auto.components_profile_hostsettings.1", "Não foi possível atualizar as configurações."), variant: "destructive" });
    },
  });

  const handleAutoReplyToggle = async (enabled: boolean) => {
    setAutoReplyEnabled(enabled);
    setIsSavingAutoReply(true);
    try {
      await updateProfile.mutateAsync({ auto_reply_enabled: enabled });
      toast({
        title: enabled ? "Resposta automática ativada" : "Resposta automática desativada",
        description: enabled
          ? "O Bera irá responder automaticamente aos hóspedes quando estiver offline."
          : "Você receberá as mensagens diretamente sem resposta automática.",
      });
    } catch {
      setAutoReplyEnabled(!enabled);
    } finally {
      setIsSavingAutoReply(false);
    }
  };

  const handleSaveAutoReplyMessage = async () => {
    setIsSavingAutoReply(true);
    try {
      await updateProfile.mutateAsync({ auto_reply_message: autoReplyMessage });
      toast({ title: "Mensagem guardada", description: tAuto("auto.components_profile_hostsettings.2", "A sua mensagem de resposta automática foi atualizada.") });
      setShowAutoReplyDialog(false);
    } catch {
      toast({ title: "Erro", description: tAuto("auto.components_profile_hostsettings.3", "Não foi possível guardar a mensagem."), variant: "destructive" });
    } finally {
      setIsSavingAutoReply(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-60 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isVerified = isProfileKycVerified(profile);
  const hasSubmittedVerification = hasProfileSubmittedKyc(profile);
  const verificationPending = isProfileVerificationPending(profile);

  /* ─── Shared section content ─── */
  const VerificationContent = () => (
    <div className="p-6">
      {isVerified ? (
        <div className="flex items-center gap-3.5 p-5 rounded-2xl bg-success/10 dark:from-emerald-950/20 dark:to-teal-950/10 border border-emerald-250/50 dark:border-emerald-900/30 shadow-sm">
          <div className="w-11 h-11 rounded-full bg-success flex items-center justify-center shadow-md shadow-emerald-200/50">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300">Conta Verificada</p>
            <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/70 mt-0.5 font-semibold">{tAuto("auto.components_profile_hostsettings.4", "Identidade confirmada com sucesso")}</p>
          </div>
        </div>
      ) : verificationPending ? (
        <div className="flex items-center gap-3.5 p-5 rounded-2xl bg-warning/10 dark:from-amber-950/20 dark:to-orange-950/10 border border-amber-250/50 dark:border-amber-900/30 shadow-sm animate-pulse">
          <div className="w-11 h-11 rounded-full bg-warning flex items-center justify-center shadow-md shadow-amber-200/50">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800 dark:text-amber-300">{tAuto("auto.components_profile_hostsettings.5", "Em Análise")}</p>
            <p className="text-[11px] text-amber-600/80 dark:text-amber-400/70 mt-0.5 font-semibold">{tAuto("auto.components_profile_hostsettings.6", "Estamos verificando os seus documentos de identidade")}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3.5 p-5 rounded-2xl bg-primary/5 border border-primary/25">
            <div className="relative shrink-0">
              <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
              <div className="relative w-11 h-11 rounded-full bg-gradient-to-br from-primary to-primary flex items-center justify-center shadow-md shadow-primary/30 animate-pulse">
                <ShieldAlert className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-primary">{tAuto("auto.components_profile_hostsettings.7", "Não Verificada")}</p>
              <p className="text-[11px] text-primary/80 mt-0.5 font-semibold">{tAuto("auto.components_profile_hostsettings.8", "Verifique a sua identidade para começar a receber reservas.")}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/perfil")}
            className="w-full h-11 gradient-primary text-white font-semibold rounded-xl shadow-[0_6px_16px_-6px_rgba(232,81,61,0.55)]"
          >
            Verificar Agora
          </Button>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed mt-1">
            A verificação de conta é um processo obrigatório para a segurança de todos os utilizadores da plataforma BeraStays.
          </p>

        </div>
      )}
    </div>
  );

  const PaymentsContent = () => (
    <div className="p-6">
      {user?.id && (
        <IbanForm
          userId={user.id}
          currentIban={(profile as any)?.iban}
          ibanConfirmedAt={(profile as any)?.iban_confirmed_at}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["profile", user?.id] })}
        />
      )}
    </div>
  );

  const BeraIaContent = () => (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between p-5 rounded-2xl bg-primary/10 border border-primary/50 dark:from-primary/20 dark:to-purple-950/10">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center shadow-md shadow-violet-200/50">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <Label className="text-sm font-bold text-primary dark:text-primary/30">{tAuto("auto.components_profile_hostsettings.9", "Resposta Automática")}</Label>
            <p className="text-[11px] text-primary/80 dark:text-primary mt-0.5 font-semibold">{tAuto("auto.components_profile_hostsettings.10", "Responde aos hóspedes quando offline")}</p>
          </div>
        </div>
        <Switch checked={autoReplyEnabled} onCheckedChange={handleAutoReplyToggle} disabled={isSavingAutoReply} />
      </div>

      <button
        onClick={() => window.dispatchEvent(new CustomEvent("open-bera-ia-widget"))}
        className="w-full flex items-center gap-3.5 p-5 rounded-2xl bg-gradient-to-r from-cyan-50 to-blue-50/80 border border-cyan-250/50 hover:shadow-md hover:shadow-cyan-100 dark:from-cyan-950/20 dark:to-blue-950/10 transition-all text-left group active:scale-[0.98]"
      >
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-400 to-blue-555 flex items-center justify-center shadow-md shadow-cyan-200/50">
          <img src="/bera-ia-avatar.png" alt="Bera" className={`rounded-full object-cover ${"w-5 h-5 text-white"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-cyan-850 dark:text-cyan-300">{tAuto("auto.components_profile_hostsettings.11", "Falar com Bera")}</p>
          <p className="text-[11px] text-cyan-600/80 dark:text-cyan-450 mt-0.5 font-semibold">Abrir assistente inteligente</p>
        </div>
        <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center group-hover:bg-cyan-200/80 transition-colors shrink-0">
          <ChevronRight className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
        </div>
      </button>

      {autoReplyEnabled && (
        <button
          onClick={() => setShowAutoReplyDialog(true)}
          className="w-full flex items-center gap-3.5 p-5 rounded-2xl bg-primary/5 border border-primary/20 hover:shadow-md hover:shadow-primary/10 transition-all text-left group active:scale-[0.98]"
        >
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-primary flex items-center justify-center shadow-md shadow-primary/20 shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-primary">Editar Mensagem</p>
            <p className="text-[11px] text-primary/80 font-semibold line-clamp-1 mt-0.5">
              {autoReplyMessage || "Nenhuma mensagem definida"}
            </p>
          </div>
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors shrink-0">
            <ChevronRight className="w-4 h-4 text-primary" />
          </div>
        </button>
      )}
    </div>
  );

  const sections = [
    { id: "conta", icon: User, label: "Conta", sublabel: "Verificação" },
    { id: "pagamentos", icon: CreditCard, label: "Pagamentos", sublabel: "IBAN e Saques" },
    { id: "bera-ia", icon: () => <img src="/bera-ia-avatar.png" className="w-5 h-5 rounded-full object-cover" alt="Bera" />, label: "Bera", sublabel: "Automação" },
    { id: "seguranca", icon: ShieldCheck, label: "Segurança", sublabel: "Verificação em dois passos" },
  ];

  const renderSectionContent = (sectionId: string) => {
    switch (sectionId) {
      case "conta": return <VerificationContent />;
      case "pagamentos": return <PaymentsContent />;
      case "bera-ia": return <BeraIaContent />;
      case "seguranca": return <div className="p-5"><SecuritySettingsCard /></div>;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Title block */}
      <div className="flex items-center gap-3.5 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center shadow-sm">
          <Settings className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-800 dark:text-zinc-100 font-display">{tAuto("auto.components_profile_hostsettings.12", "Configurações do Dashboard")}</h2>
          <p className="text-xs text-zinc-450 dark:text-zinc-550 font-semibold mt-0.5">{tAuto("auto.components_profile_hostsettings.13", "Gira as preferências e payouts do seu perfil de proprietário")}</p>
        </div>
      </div>

      {isMobile ? (
        <Accordion type="single" collapsible defaultValue="conta" className="space-y-3">
          {sections.map((section) => (
            <AccordionItem key={section.id} value={section.id} className="border-none">
              <AccordionTrigger className="px-5 py-4 rounded-2xl bg-white dark:bg-[#151518]/60 border border-gray-150 dark:border-zinc-800/40 hover:no-underline hover:bg-gray-50/50 dark:hover:bg-zinc-800/35 data-[state=open]:rounded-b-none data-[state=open]:border-b-0 [&[data-state=open]>svg]:rotate-180 transition-all font-sans">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-gray-800 dark:text-zinc-100">{section.label}</p>
                    <p className="text-[10px] text-zinc-400 dark:text-zinc-500 font-semibold mt-0.5">{section.sublabel}</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-white dark:bg-[#151518]/60 border border-t-0 border-gray-150 dark:border-zinc-800/40 rounded-b-2xl p-0">
                {renderSectionContent(section.id)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="grid grid-cols-[220px_1fr] gap-6 items-start">
          {/* Sub Sidebar */}
          <div className="space-y-1.5 bg-white dark:bg-[#151518]/30 border border-gray-100 dark:border-zinc-850/40 rounded-3xl p-3 shadow-sm">
            {sections.map((section) => (
              <SidebarItem
                key={section.id}
                icon={section.icon}
                label={section.label}
                active={activeSection === section.id}
                onClick={() => setActiveSection(section.id)}
              />
            ))}
          </div>

          {/* Sub Content */}
          <SectionCard>
            <SectionHeader
              icon={sections.find(s => s.id === activeSection)!.icon}
              title={sections.find(s => s.id === activeSection)!.label}
              subtitle={sections.find(s => s.id === activeSection)!.sublabel}
            />
            {renderSectionContent(activeSection)}
          </SectionCard>
        </div>
      )}

      {/* Auto-Reply Dialog */}
      <Dialog open={showAutoReplyDialog} onOpenChange={setShowAutoReplyDialog}>
        <DialogContent className="max-w-md p-5 rounded-3xl border border-gray-100 dark:border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm font-bold font-display">
              <img src="/bera-ia-avatar.png" alt="Bera" className={`rounded-full object-cover ${"w-5 h-5 text-primary"}`} />
              Mensagem Automática (Bera)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-zinc-500">{tAuto("auto.components_profile_hostsettings.14", "Defina a mensagem padrão para enviar quando estiver offline:")}</Label>
              <Textarea
                placeholder={tAuto("auto.components_profile_hostsettings.15", "Ex: Olá! Recebemos a sua mensagem. Responderemos o mais breve possível. O nosso horário de check-in é a partir das 14h...")}
                value={autoReplyMessage}
                onChange={(e) => setAutoReplyMessage(e.target.value)}
                className="min-h-[120px] resize-none text-xs rounded-2xl border-gray-200 focus:border-primary/50 focus:ring-primary/20"
                maxLength={500}
              />
              <p className="text-[10px] text-zinc-450 dark:text-zinc-550 text-right font-bold">{autoReplyMessage.length}/500 caracteres</p>
            </div>
            <Button onClick={handleSaveAutoReplyMessage} disabled={isSavingAutoReply} className="w-full bg-primary hover:bg-[#D14430] text-white rounded-xl text-xs font-bold">
              {isSavingAutoReply && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Guardar Mensagem
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
